import { FC } from 'react';
import { FormikErrors } from 'formik';
import { i18n as I18n, TFunction } from 'i18next';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { AxiosResponse } from 'axios';
import { AnalyticsResponseDto } from '@api/models/analytics/AnalyticsResponseDto';
import { AnalyticsGroupDto } from '@api/models/analytics/AnalyticsGroupDto';
import { AnalyticsRowDto } from '@api/models/analytics/AnalyticsRowDto';
import { AnalyticsDomain } from '@api/models/enums/AnalyticsDomain';
import { TotalsMetricConfig, TotalsMetricKey } from './analyticsTotalsConfig';
import type { ExportOption } from '../common/basic/QcsExportButton';
import { Args } from '@api/services/BaseService';

export type GroupByOptionGroup = 'entity' | 'time';

export interface GroupByOption {
  value: string;
  labelKey: string;
  group?: GroupByOptionGroup;
}

export const GROUP_SUBHEADER_KEYS: Record<GroupByOptionGroup, string> = {
  entity: 'analytics.groupBy.entityGroup',
  time: 'analytics.groupBy.timeGroup',
};

/** Sentinel group-by value meaning "no grouping" (i.e. show ungrouped rows). */
export const NO_GROUP_BY = 'NONE';

/**
 * Pluggable configuration for a single analytics domain (e.g. visitations).
 *
 * Each domain provides its own form values, API integration, serialization,
 * and UI components. Consumers treat `TFormValues` as opaque — values flow
 * in a closed loop within one domain.
 */
export interface AnalyticsDomainConfig<TFormValues extends Args = Args> {
  // ── Identity ────────────────────────────────────────────────────────

  /** URL-safe slug used for routing (`/analytics/{key}`). */
  key: string;
  /** Backend API enum identifying this domain. */
  domain: AnalyticsDomain;
  /** Page heading shown when this domain is active. Falls back to `mainMenu.analytics`. */
  pageTitleKey?: string;
  /** Metadata for the domain selection card on the analytics landing page. */
  card: {
    icon: IconProp;
    titleKey: string;
    descriptionKey: string;
  };

  // ── Group-by ────────────────────────────────────────────────────────

  /** Available grouping dimensions shown in the group-by dropdown. */
  groupByOptions: GroupByOption[];
  /** Maps each group-by value to an i18n key used as the dashboard tile title. */
  groupByTitleKeys: Record<string, string>;
  /** Group-by value used when none is specified (e.g. first load, URL fallback). */
  defaultGroupBy: string;
  /** Group-by values to hide when the user has access to a single establishment only. */
  singleEstablishmentHiddenGroupBys?: string[];

  // ── KPI totals ──────────────────────────────────────────────────────

  /**
   * Per-totals-metric labels and formatting used by KPI tiles and the KPI metric picker.
   * Each domain decides what its `count`/`sum`/etc. semantically mean (visitation =
   * durations, payments = currency, etc.) and how to label them.
   */
  totalsMetrics: Record<TotalsMetricKey, TotalsMetricConfig>;

  // ── Form values & API ───────────────────────────────────────────────

  /** Factory that returns the default (empty) filter form state. */
  defaultFormValues: () => TFormValues;
  /** Convert form values + group-by into query params for the grouped analytics API call. */
  toQueryParams: (values: TFormValues, groupBy: string) => Args;
  /** Convert form values + pagination into query params for the drill-down rows API call. */
  toDrillDownQueryParams: (values: TFormValues, pagination: { page: number; size: number }) => Args;
  /** Execute the analytics API call. */
  fetchAnalytics: (params: Args) => Promise<AnalyticsResponseDto>;
  /** Execute the analytics export API call. When defined, the export button is shown in table view. */
  fetchExport?: (params: Args) => Promise<AxiosResponse<Blob>>;
  /** Available export format options. When defined, the export button shows a format picker. */
  exportOptions?: ExportOption[];

  // ── Drill-down & display ────────────────────────────────────────────

  /** Narrow form values when the user clicks a group row to drill down. Returns `undefined` if drill-down is not applicable. */
  applyDrillDown: (formValues: TFormValues, groupBy: string, group: AnalyticsGroupDto) => TFormValues | undefined;
  /** Build human-readable filter tag strings for the active filters summary. */
  buildFilterTags: (values: TFormValues, t: TFunction, i18n: I18n, context: { establishmentCount: number }) => string[];

  // ── Serialization ───────────────────────────────────────────────────

  /** Serialize form values for storage in a dashboard tile. */
  toTileFilters: (values: TFormValues) => Args;
  /** Deserialize dashboard tile filters back to form values. */
  fromTileFilters: (filters: Args) => TFormValues;
  /** Serialize form values to URL query-string key-value pairs. */
  toUrlParams: (values: TFormValues) => Record<string, string>;
  /** Every URL query-string key this domain may emit via toUrlParams. Used to strip stale values when a filter is cleared. */
  urlParamKeys: readonly string[];
  /** Deserialize URL query params back to form values. */
  fromUrlParams: (params: URLSearchParams) => TFormValues;

  // ── Validation ──────────────────────────────────────────────────────

  /**
   * Optional Formik validate function for the filter form. When a domain provides
   * this, Apply gates on Formik's `isValid` (errors object empty). Messages must
   * be i18n keys translated here at validation time.
   */
  validate?: (values: TFormValues, t: TFunction) => FormikErrors<TFormValues>;

  // ── UI ──────────────────────────────────────────────────────────────

  /** Domain-specific filter bar rendered inside the Formik form context. */
  FilterBarComponent: FC;

  // ── Drill-down row interaction ──────────────────────────────────────

  /** Target URL for the "fullscreen" navigation from the drill-down row drawer. */
  getRowHref?: (row: AnalyticsRowDto) => string | undefined;
  /** Identifier passed into the drawer body for the clicked drill-down row. */
  getRowDrawerId?: (row: AnalyticsRowDto) => string | undefined;
  /** Domain-specific component rendered inside the drill-down row's right-side drawer. */
  DrawerBodyComponent?: FC<{ id: string }>;
}
